import { NativeModules } from 'react-native';
import Config from 'react-native-config';
import { debounce, throttle, memoize } from 'lodash';
import { isAndroid, isBlank, isPresent, isWeb } from '../../utils/BooleanUtility';
import { getStoreRef } from '../../store/StoreUtility';
import { saveScheduledNotificationIds } from '../../actions/RoutineActions';
import { IST_OFFSET, NOTIFICATION_IDENTIFIERS, APP_INTENT_SCHEME, API_DOMAIN } from '../../config/Constants';
import { fetchApi } from '../../utils/ApiCallingUtility';
import { processBatches } from '../../utils/BatchUtility'; // Use the updated utility

const JSONAPIDeserializer = require('jsonapi-serializer').Deserializer;

const NOTIFICATION_CONFIG = {
  BATCH_SIZE: 20,
  THROTTLE_DELAY: 200,
  API_BATCH_SIZE: 3,
  REMINDER_MINUTES: 5,
};

const calculateSecondsToStart = memoize((startDateTimeString = '') => {
  if (!startDateTimeString) return 0;

  const startDateTime = new Date(new Date(startDateTimeString).getTime() + IST_OFFSET);
  const fiveMinutesBeforeStart = new Date(startDateTime.getTime() - NOTIFICATION_CONFIG.REMINDER_MINUTES * 60 * 1000);
  const currentDateTime = new Date();
  const currentISTDateTime = new Date(currentDateTime.getTime() + IST_OFFSET);

  return Math.floor(Math.max(0, fiveMinutesBeforeStart - currentISTDateTime) / 1000);
});

const getIndicatorPercentage = (indicatorPercentage) => {
  if (isBlank(indicatorPercentage)) return 0;
  return isAndroid() ? indicatorPercentage : indicatorPercentage / 100;
};

export const createNotificationPayload = memoize(({ entityNotification, payload, item }) => {
  if (!isPresent(entityNotification) || !isPresent(payload)) {
    return { scheduledNotifications: [], notificationId: [] };
  }

  const scheduledNotifications = [];
  const notificationIds = [];
  const { indicatorPercentage = 0 } = entityNotification;
  const activityId = item?.id;

  const notificationPayload = {
    ...payload,
    indicatorPercentage: getIndicatorPercentage(indicatorPercentage),
    destinationUrl: `${APP_INTENT_SCHEME}/${payload?.destinationUrl}`,
  };

  if (payload?.descriptionImage) notificationPayload.descriptionImage = payload.descriptionImage;
  if (payload?.indicatorImage) notificationPayload.indicatorImage = payload.indicatorImage;

  const notificationTime = isAndroid()
    ? new Date(item?.startsAt).getTime() - NOTIFICATION_CONFIG.REMINDER_MINUTES * 60 * 1000
    : calculateSecondsToStart(item?.startsAt);

  scheduledNotifications.push({
    notificationTime,
    payload: notificationPayload,
    activityId,
    progressColor: Config.NOTIFICATION_THEME_COLOR,
    ctaColor: Config.NOTIFICATION_THEME_COLOR,
  });

  notificationIds.push(activityId);

  return { scheduledNotifications, notificationId: notificationIds };
});

const processNotificationBatch = memoize(async (notifications, notificationIds) => {
  const store = getStoreRef();
  store.dispatch(saveScheduledNotificationIds(notificationIds));

  if (isAndroid()) {
    const notificationString = JSON.stringify(notifications);
    await NativeModules.ScheduledLocalNotification.scheduleLocalNotification(notificationString);
  } else {
    const notificationData = {
      routineNotification: true,
      scheduledNotifications: notifications,
    };
    await NativeModules.LocalNotificationHandler.createNotification(
      NOTIFICATION_IDENTIFIERS.ROUTINE_ACTIVITY_NOTIFICATION,
      notificationData,
    );
  }
});

const scheduleNotificationsInBatches = throttle(
  async (notifications, notificationIds) => {
    if (!isPresent(notifications)) return;

    await processBatches(notifications, async (batch) => processNotificationBatch(batch, notificationIds), {
      batchSize: NOTIFICATION_CONFIG.BATCH_SIZE,
    });
  },
  NOTIFICATION_CONFIG.THROTTLE_DELAY,
  { leading: true, trailing: false },

);

export const scheduleNotifications = (routineDaysData) => {
  if (isWeb() || isBlank(routineDaysData)) return;

  const notifications = [];
  const notificationIds = [];

  routineDaysData.forEach((activity = {}) => {
    const { entityNotification = {}, entityNotification: { notificationConfig: payload = {} } = {} } = activity;

    const { scheduledNotifications, notificationId } = createNotificationPayload({
      entityNotification,
      payload,
      item: activity,
    });

    notifications.push(...scheduledNotifications);
    notificationIds.push(...notificationId);
  });
  
  scheduleNotificationsInBatches(notifications, notificationIds);
};

export const scheduleChatsNotification = debounce(
  (data) => {
    if (isWeb() || isBlank(data)) return;

    const notifications = [];
    const notificationIds = [];

    data.forEach((item = {}) => {
      const { entityNotification = {} } = item;
      if (isPresent(entityNotification)) {
        const { scheduledNotifications, notificationId } = createNotificationPayload({
          entityNotification,
          payload: item.entityNotification.notificationConfig || {},
          item,
        });
        notifications.push(...scheduledNotifications);
        notificationIds.push(...notificationId);
      }
    });

    scheduleNotificationsInBatches(notifications, notificationIds);
  },
  NOTIFICATION_CONFIG.THROTTLE_DELAY,
  { leading: false, trailing: true },
);

export const cancelNotificationsByActivityId = (activityId) => {
  if (isWeb()) return;

  if (isAndroid()) {
    NativeModules.ScheduledLocalNotification.cancelScheduledNotificationsByActivityId(activityId);
  } else {
  }
};

/**
 * Fetch notification details for a routine
 * @param {string} slug - Routine slug
 * @returns {Promise<Array|null>} - Notification data
 */
export const fetchRoutineNotificationsDetail = async (slug) => {
  try {
    const url = `${API_DOMAIN}/api/v3/routines/${slug}/notifications`;
    const response = await fetchApi(url);

    if (response) {
      const data = await new JSONAPIDeserializer({
        typeAsAttribute: false,
        pluralizeType: true,
        keyForAttribute: 'camelCase',
      }).deserialize(response);

      scheduleNotifications(data);
      return data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const executeRoutineNotifications = async (slugs) => {
  if (isBlank(slugs)) {
    return [];
  }

  return processBatches(
    slugs,
    async (slug) => {
      if (isBlank(slug)) return null;
      return fetchRoutineNotificationsDetail(slug);
    },
    { batchSize: NOTIFICATION_CONFIG.API_BATCH_SIZE, concurrent: true },
  );
};
